import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe, Plus, Shuffle, Trash2 } from "lucide-react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WizardPanel, type DomainRow } from "@/components/domain-wizard/WizardPanel";
import { ReassignDialog } from "@/components/domain-wizard/ReassignDialog";
import { DeleteDialog } from "@/components/domain-wizard/DeleteDialog";

const STATE_LABEL: Record<DomainRow["wizardState"], string> = {
  awaiting_dns: "Awaiting DNS",
  verifying: "Verifying",
  dns_verified: "DNS verified",
  issuing: "Issuing TLS",
  assigning: "Pick target",
  live: "Live",
  failed: "Failed",
};

const STATE_TONE: Record<DomainRow["wizardState"], string> = {
  awaiting_dns: "bg-attention-subtle text-attention border-attention/30",
  verifying: "bg-attention-subtle text-attention border-attention/30",
  dns_verified: "bg-success-subtle text-success border-success/30",
  issuing: "bg-attention-subtle text-attention border-attention/30",
  assigning: "bg-accent-subtle text-accent border-accent/30",
  live: "bg-success-subtle text-success border-success/30",
  failed: "bg-danger-subtle text-danger border-danger/30",
};

export function ProjectDomains() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const openDomainId = searchParams.get("domain");

  const list = useQuery({
    enabled: !!org && !!project,
    queryKey: ["project-domains", org, project],
    queryFn: () =>
      api.get<{ domains: DomainRow[] }>(`/orgs/${org}/projects/${project}/domains`),
  });

  const open = openDomainId !== null;
  const newPanel = open && openDomainId === "new";

  const closePanel = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("domain");
    setSearchParams(next, { replace: true });
  };

  const openNew = () => {
    const next = new URLSearchParams(searchParams);
    next.set("domain", "new");
    setSearchParams(next, { replace: true });
  };

  const openExisting = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("domain", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="of-card">
        <div className="of-card-header">
          <div className="flex items-center gap-2">
            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Custom domains</h3>
            <span className="text-2xs text-subtle-foreground">{list.data?.domains.length ?? 0}</span>
          </div>
          {!open && (
            <Button variant="primary" size="sm" onClick={openNew}>
              <Plus className="h-3.5 w-3.5" />
              Add domain
            </Button>
          )}
        </div>
        {list.isLoading && <Empty>Loading…</Empty>}
        {!list.isLoading && (list.data?.domains.length ?? 0) === 0 && !open && (
          <Empty>
            <div>No custom domains attached yet.</div>
            <div className="text-2xs mt-2">
              Add one above and we'll walk you through DNS + TLS.
            </div>
          </Empty>
        )}
        {list.data?.domains.map((d) => (
          <Row
            key={d.id}
            row={d}
            isOpen={openDomainId === d.id}
            onContinue={() => openExisting(d.id)}
            orgSlug={org!}
            projectSlug={project!}
            onDeleted={(deletedId) => {
              // If the deleted row's panel is currently open, close it.
              if (openDomainId === deletedId) closePanel();
            }}
          />
        ))}
        {!open && (list.data?.domains.length ?? 0) > 0 && (
          <div className="px-4 py-3 border-t border-border-muted">
            <Button variant="ghost" size="sm" onClick={openNew}>
              <Plus className="h-3.5 w-3.5" />
              Add another
            </Button>
          </div>
        )}
      </div>

      {open && org && project && (
        <WizardPanel
          orgSlug={org}
          projectSlug={project}
          rowId={newPanel ? null : openDomainId}
          onClose={closePanel}
          onOpenId={openExisting}
        />
      )}
    </div>
  );
}

function Row({
  row,
  isOpen,
  onContinue,
  orgSlug,
  projectSlug,
  onDeleted,
}: {
  row: DomainRow;
  isOpen: boolean;
  onContinue: () => void;
  orgSlug: string;
  projectSlug: string;
  onDeleted: (id: string) => void;
}) {
  const [reassignOpen, setReassignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  return (
    <div className="of-row gap-3 items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm truncate">{row.hostname}</span>
          {row.displayHostname && (
            <span className="text-2xs text-subtle-foreground">({row.displayHostname})</span>
          )}
          <span
            className={
              "text-2xs uppercase tracking-wider border rounded px-1.5 py-0.5 font-mono " +
              STATE_TONE[row.wizardState]
            }
          >
            {STATE_LABEL[row.wizardState]}
          </span>
          {row.healthWarning && row.wizardState === "live" && (
            <span
              title="DNS no longer points to the platform"
              className="text-2xs uppercase tracking-wider bg-attention-subtle text-attention border border-attention/30 rounded px-1.5 py-0.5"
            >
              DNS drift
            </span>
          )}
        </div>
        {row.target && row.wizardState === "live" && (
          <div className="text-2xs text-subtle-foreground mt-0.5">
            → {row.target.type}
            {row.target.branch ? <> (<span className="font-mono">{row.target.branch}</span>)</> : null}
          </div>
        )}
      </div>
      {row.wizardState !== "live" && !isOpen && (
        <Button size="sm" variant="default" onClick={onContinue}>
          Continue
        </Button>
      )}
      {row.wizardState === "live" && (
        <>
          <Button size="sm" variant="ghost" onClick={() => setReassignOpen(true)}>
            <Shuffle className="h-3.5 w-3.5" />
            Reassign
          </Button>
          <ReassignDialog
            open={reassignOpen}
            onOpenChange={setReassignOpen}
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            rowId={row.id}
            hostname={row.hostname}
            current={{ type: row.target?.type ?? null, branch: row.target?.branch ?? null }}
          />
        </>
      )}
      <Button
        size="icon"
        variant="ghost"
        className="text-danger"
        onClick={() => setDeleteOpen(true)}
        title={row.wizardState === "live" ? "Remove live hostname" : "Remove hostname"}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        orgSlug={orgSlug}
        projectSlug={projectSlug}
        rowId={row.id}
        hostname={row.hostname}
        isLive={row.wizardState === "live"}
        onDeleted={() => onDeleted(row.id)}
      />
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>
  );
}
